import { apiService } from '../api';
import {
  PromotionRun,
  PromotionRunStatusResponse,
  PromotionPreviewRow,
  PromotionDecision,
  Pagination
} from '../../types/sessions';

export interface StartRunInput {
  sourceSessionId: string;
  targetSessionId: string;
}

export interface DecisionInput {
  enrollmentId: string;
  promotionDecision: Exclude<PromotionDecision, 'pending'>;
  manualTargetClassId?: string | null;
  sectionInstanceIdForNext?: string | null;
  remarks?: string;
}

export interface CommitChunkInput {
  classId: string;
  enrollmentIds?: string[];     // omit → commit all decided in class
}

export interface FinalizeInput {
  leftoverDisposition?: 'promote' | 'retain' | 'drop' | 'inconclusive';
}

export const promotionsApi = {
  listForSession: async (sessionId: string) => {
    const response = await apiService.get<PromotionRun[]>('/promotion-runs', { sessionId });
    return response.data ?? [];
  },

  start: async (input: StartRunInput) => {
    const response = await apiService.post<PromotionRun>('/promotion-runs', input);
    return response.data!;
  },

  getStatus: async (id: string) => {
    const response = await apiService.get<PromotionRunStatusResponse>(`/promotion-runs/${id}/status`);
    return response.data!;
  },

  previewByClass: async (id: string, classId: string, page?: number, limit?: number) => {
    const response = await apiService.get<PromotionPreviewRow[]>(
      `/promotion-runs/${id}/preview`,
      { classId, page, limit }
    );
    return {
      data: response.data ?? [],
      pagination: (response as any).pagination as Pagination | undefined,
      classId
    };
  },

  applyDecisions: async (id: string, decisions: DecisionInput[]) => {
    const response = await apiService.put<{ updated: number }>(
      `/promotion-runs/${id}/decisions`,
      decisions
    );
    return response.data!;
  },

  resetDecisions: async (id: string, enrollmentIds?: string[]) => {
    const response = await apiService.post<{ reset: number }>(
      `/promotion-runs/${id}/reset-decisions`,
      enrollmentIds ? { enrollmentIds } : {}
    );
    return response.data!;
  },

  commitChunk: async (id: string, input: CommitChunkInput) => {
    const response = await apiService.post<{
      chunkId: string;
      outcome: { promoted: number; retained: number; graduated: number; dropped: number; inconclusive: number; manualOverrides: number };
      createdEnrollmentIds: string[];
    }>(`/promotion-runs/${id}/commit-chunk`, input);
    return response.data!;
  },

  retryChunk: async (id: string, chunkId: string) => {
    const response = await apiService.post<any>(`/promotion-runs/${id}/chunks/${chunkId}/retry`);
    return response.data!;
  },

  finalize: async (id: string, input: FinalizeInput = {}) => {
    const response = await apiService.post<PromotionRun>(`/promotion-runs/${id}/finalize`, input);
    return response.data!;
  },

  cancel: async (id: string) => {
    const response = await apiService.post<PromotionRun>(`/promotion-runs/${id}/cancel`);
    return response.data!;
  }
};
